import { DownloadOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Col, Descriptions, Row, Skeleton, Space, Statistic, Table, Tag, message } from 'antd';
import { downloadFile, errorMessage } from '../../api/client';
import { useFixedAccount } from '../../api/hooks';
import type { FixedLedgerEntry, PartyType } from '../../types';
import { date, money } from '../../utils/format';
import { useTranslation } from 'react-i18next';

const TYPE_COLOR: Record<string, string> = {
  Investment: 'blue',
  Withdrawal: 'orange',
};

interface Props {
  partyType: PartyType;
  partyId: number;
}

/**
 * The fixed-return ledger — every investment and withdrawal in date order with a running balance.
 * Laid out like the percentage Ledger tab so both read the same way, but the columns describe an
 * agreed total being drawn down rather than capital plus accruing profit.
 */
export default function FixedLedgerTab({ partyType, partyId }: Props) {
  const { t } = useTranslation();
  const { data: account, isLoading, error } = useFixedAccount(partyType, partyId);

  if (error) return <Alert type="error" showIcon message={errorMessage(error)} />;
  if (isLoading || !account) return <Skeleton active paragraph={{ rows: 6 }} />;

  const statementPath =
    partyType === 'Investor'
      ? `/reports/fixed-statement/investor/${partyId}`
      : `/reports/fixed-statement/dealer/${partyId}`;

  return (
    <>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() =>
            downloadFile(statementPath, `fixed_statement_${partyId}.pdf`).catch((err) =>
              message.error(errorMessage(err)),
            )
          }
        >
          Download statement (PDF)
        </Button>
      </Space>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12} lg={6}>
          <Card>
            <Statistic title={t('finance.totalInvestment')} value={money(account.totalInvested)} prefix="PKR" />
          </Card>
        </Col>
        <Col xs={24} md={12} lg={6}>
          <Card>
            <Statistic title={t('finance.totalReturnAgreed')} value={money(account.totalReturnAgreed)} prefix="PKR" />
          </Card>
        </Col>
        <Col xs={24} md={12} lg={6}>
          <Card>
            <Statistic
              title="Total payable amount"
              value={money(account.totalRemaining)}
              prefix="PKR"
              valueStyle={{ color: account.totalRemaining > 0 ? '#FF0000' : '#3EB780' }}
            />
          </Card>
        </Col>
        <Col xs={24} md={12} lg={6}>
          <Card>
            {/* The agreed return less what is still payable — which is exactly what has been paid
                out, so the stored total is used rather than re-deriving the same subtraction. */}
            <Statistic title="Withdrawal amount" value={money(account.totalWithdrawn)} prefix="PKR" />
          </Card>
        </Col>
      </Row>

      {/* Breakpoints spelled out to md and above: antd merges this over its own default map
          (lg/xl/xxl = 3), so naming only `md` would silently fall back to 3 on wide screens. */}
      <Descriptions
        bordered
        size="small"
        column={{ xs: 1, sm: 2, md: 4, lg: 4, xl: 4, xxl: 4 }}
        style={{ marginTop: 16 }}
      >
        <Descriptions.Item label="Total profit agreed">{money(account.totalProfit)}</Descriptions.Item>
        <Descriptions.Item label={t('finance.totalWithdrawn')}>{money(account.totalWithdrawn)}</Descriptions.Item>
        <Descriptions.Item label="Investments on record">{account.investments.length}</Descriptions.Item>
        <Descriptions.Item label="Next due">
          {account.nextDueDate ? date(account.nextDueDate) : '—'}
        </Descriptions.Item>
      </Descriptions>

      <Card title={t('table.transactions')} size="small" style={{ marginTop: 16 }}>
        <Table<FixedLedgerEntry>
          rowKey={(row) => `${row.type}-${row.refId}`}
          size="small"
          dataSource={account.ledger}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 25, showSizeChanger: true }}
          columns={[
            { title: t('common.date'), dataIndex: 'date', render: date },
            {
              title: t('common.type'),
              dataIndex: 'type',
              render: (v: string) => <Tag color={TYPE_COLOR[v] ?? 'default'}>{v}</Tag>,
            },
            { title: t('party.description'), dataIndex: 'description' },
            {
              title: 'Investment',
              dataIndex: 'invested',
              align: 'right',
              render: (v: number) => (v ? money(v) : '—'),
            },
            {
              title: 'Return Agreed Amount',
              dataIndex: 'returnAgreed',
              align: 'right',
              render: (v: number) => (v ? money(v) : '—'),
            },
            {
              title: t('finance.withdrawn'),
              dataIndex: 'withdrawn',
              align: 'right',
              render: (v: number) => (v ? money(v) : '—'),
            },
            {
              title: t('finance.remaining'),
              dataIndex: 'remainingAfter',
              align: 'right',
              render: (v: number) => <strong>{money(v)}</strong>,
            },
          ]}
        />
      </Card>
    </>
  );
}
